import {
  MilestoneRewardType,
  MilestoneType,
  type IMilestoneDetail,
  type ObjectiveBasedCampaignDto,
} from '@/interfaces/objectiveBasedCampaign';

const bdt = (n: number): string => `৳${n.toLocaleString('en-IN')}`;

const clampNonNegative = (n: number | null | undefined): number =>
  typeof n === 'number' && n > 0 ? n : 0;

const rewardOff = (m: IMilestoneDetail): string => {
  if (m.RewardType === MilestoneRewardType.Percentage) {
    return `${m.RewardValue}% OFF`;
  }

  return `${bdt(m.RewardValue)} OFF`;
};

const progressCopy = (m: IMilestoneDetail): string => {
  if (m.MilestoneType === MilestoneType.PurchaseCount) {
    const remaining = clampNonNegative(m.RemainingPurchaseCount);

    if (remaining > 0) {
      return `<strong>Not yet unlocked</strong><br/><br/>Lifetime Reward<br/>Place ${remaining} more order${remaining === 1 ? '' : 's'} to unlock ${rewardOff(m)}!`;
    }
  } else {
    const remaining = clampNonNegative(m.RemainingPurchaseAmount);

    if (remaining > 0) {
      return `<strong>Not yet unlocked</strong><br/><br/>Lifetime Reward<br/>Spend ${bdt(remaining)} more to unlock ${rewardOff(m)}!`;
    }
  }

  const required = m.RequiredPurchaseCount ?? m.RequiredPurchaseAmount ?? m.TargetValue;
  const verb = m.MilestoneType === MilestoneType.PurchaseCount ? 'Place' : 'Spend';

  if (m.MilestoneType === MilestoneType.PurchaseCount) {
    return `<strong>Not yet unlocked</strong><br/><br/>Lifetime Reward<br/>${verb} ${required} more orders to unlock ${rewardOff(m)}!`;
  }

  return `<strong>Not yet unlocked</strong><br/><br/>Lifetime Reward<br/>${verb} ${bdt(required)} more to unlock ${rewardOff(m)}!`;
};

const achievedCopy = (m: IMilestoneDetail): string => {
  if (m.RewardType === MilestoneRewardType.Percentage) {
    if (m.MilestoneType === MilestoneType.LifetimeSpend) {
      return `<strong>Unlocked — percentage discount</strong><br/><br/>Lifetime Reward Unlocked!<br/>You've unlocked ${m.RewardValue}% OFF.`;
    }

    return `<strong>Unlocked — percentage discount</strong><br/><br/>Lifetime Reward Unlocked!<br/>Your ${m.RewardValue}% OFF reward is ready.`;
  }

  if (m.MilestoneType === MilestoneType.LifetimeSpend) {
    return `<strong>Unlocked — fixed discount</strong><br/><br/>Lifetime Reward Unlocked!<br/>You've unlocked ${bdt(m.RewardValue)} OFF.`;
  }

  return `<strong>Unlocked — fixed discount</strong><br/><br/>Lifetime Reward Unlocked!<br/>Your ${bdt(m.RewardValue)} OFF reward is ready.`;
};

export const formatMilestoneNotice = (
  campaign: ObjectiveBasedCampaignDto,
  milestones: IMilestoneDetail[],
): string => {
  const milestone = milestones[0];

  if (!milestone) {
    return campaign.DiscountName;
  }

  if (campaign.IsAchieved) {
    return achievedCopy(milestone);
  }

  return progressCopy(milestone);
};

export const isMilestoneRuleDetails = (
  rd: unknown,
): rd is { Milestones: IMilestoneDetail[] } => {
  if (!rd || typeof rd !== 'object') {
    return false;
  }

  return Array.isArray((rd as { Milestones?: unknown }).Milestones);
};